/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * How a board is laid out on a phone: one joined row of icon buttons.
 *
 * Visitas had this control and Atendimentos did not, so the same board,
 * viewed on the same phone, offered three ways to read it on one screen and
 * one on the other. Shared rather than copied: two of these drifting apart
 * is exactly the difference somebody would have to notice, and nobody does
 * until the two screens are side by side.
 *
 * Icons without labels, because four labels do not fit a 400px row beside a
 * result count. Each carries its name as `aria-label`, and the icons are
 * drawn from what the layout does to the cards — a row of tabs, a stack of
 * rows, a grid of panels, a table.
 */

import type { ReactNode } from 'react';
import { UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

export interface MobileLayoutOption<T extends string> {
  value: T;
  /** Read aloud, and shown on hover; never printed beside the icon. */
  label: string;
  icon: ReactNode;
}

interface MobileLayoutSwitcherProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: MobileLayoutOption<T>[];
  /** Kept stable across features so existing tests keep their handle. */
  testId?: string;
  ariaLabel?: string;
}

/**
 * MobileLayoutSwitcher component.
 */
export function MobileLayoutSwitcher<T extends string>({
  value, onChange, options, testId = 'view-mode-toggle', ariaLabel,
}: MobileLayoutSwitcherProps<T>) {
  return (
    <div
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
      className="flex shrink-0 overflow-hidden rounded-(--radius-md) border border-[var(--border-subtle)]"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <UnstyledButton
            key={option.value}
            type="button"
            aria-label={option.label}
            title={option.label}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center justify-center gap-(--space-1) px-(--space-3) py-(--space-2)',
              'text-xs font-semibold transition-all duration-(--duration-fast)',
              active
                ? 'bg-[var(--seed-base)] text-white'
                : 'bg-[var(--surface-primary)] text-[var(--text-secondary)] hover:bg-[var(--surface-secondary)]',
            )}
          >
            {option.icon}
          </UnstyledButton>
        );
      })}
    </div>
  );
}

export default MobileLayoutSwitcher;
